import React, {useState} from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import type {DragEndEvent} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';

import {addToast, Anchor, Banner, Button, ButtonGroup, Heading, Icon} from '@albert-io/atomic';

import sessionStore from 'client/Session/SessionStore';

import type {SubjectFaQsQuery} from '../SubjectFAQList.queries.generated';
import {
  useReorderSubjectFaqsMutation,
  SubjectFaQsDocument
} from '../SubjectFAQList.queries.generated';

type SubjectFaq = NonNullable<
  NonNullable<SubjectFaQsQuery['subjects']>['results']
>[number]['subjectFaqs'][number];

interface SubjectFAQQuestionsProps {
  subjectFaqs: SubjectFaq[];
  subjectId: string;
  slug: string;
  isRearrangeMode: boolean;
  onSetRearrangeMode: (mode: boolean) => void;
}

const SortableFAQ = ({subjectFAQ}: {subjectFAQ: SubjectFaq}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({id: subjectFAQ.id});
  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`list-none pb-x2 content-link ${isDragging ? 'z-10' : ''}`}
    >
      <span className='pr-x2'>
        <button
          type='button'
          aria-label='Reorder'
          ref={setActivatorNodeRef}
          className='p-0 cursor-grab border-0 bg-transparent'
          {...attributes}
          {...listeners}
        >
          <Icon icon='arrows-v' />
        </button>
      </span>
      <Anchor as='span'>{subjectFAQ.question || '(ANSWER-ONLY FAQ)'}</Anchor>
    </li>
  );
};

const SortableFAQList = ({
  subjectFAQs,
  onSortEnd
}: {
  subjectFAQs: SubjectFaq[];
  onSortEnd: (sort: {oldIndex: number; newIndex: number}) => void;
}) => {
  const items = subjectFAQs.map((faq) => faq.id);

  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: 5}}),
    useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
  );

  const handleDragEnd = ({active, over}: DragEndEvent): void => {
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = items.indexOf(active.id as string);
    const newIndex = items.indexOf(over.id as string);
    if (oldIndex !== -1 && newIndex !== -1) {
      onSortEnd({oldIndex, newIndex});
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <ul className='subject-faq-question__list'>
          {subjectFAQs.map((subjectFAQ) => (
            <SortableFAQ subjectFAQ={subjectFAQ} key={subjectFAQ.id} />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
};

const SubjectFAQQuestions = ({
  subjectFaqs,
  subjectId,
  slug,
  isRearrangeMode,
  onSetRearrangeMode
}: SubjectFAQQuestionsProps) => {
  const [orderedFaqs, setOrderedFaqs] = useState<SubjectFaq[]>([]);
  const [reorderFaqs, {loading: savePending}] = useReorderSubjectFaqsMutation({
    refetchQueries: [{query: SubjectFaQsDocument, variables: {slug}}]
  });

  const setRearrangeMode = () => {
    setOrderedFaqs([...subjectFaqs]);
    onSetRearrangeMode(true);
  };

  const discardChanges = () => {
    setOrderedFaqs([]);
    onSetRearrangeMode(false);
  };

  const saveFAQOrder = async () => {
    try {
      const newOrder = orderedFaqs.map((faq) => faq.id);
      await reorderFaqs({variables: {subjectId, newOrder}});
      onSetRearrangeMode(false);
      setOrderedFaqs([]);
      addToast({
        color: 'positive',
        title: 'Subject FAQ Order Saved!',
        message: 'Subject FAQ Order has been successfully saved!'
      });
    } catch {
      addToast({
        color: 'negative',
        title: 'Subject FAQ Order Save Failed!',
        message:
          'Please try refreshing the page and rearranging again. If the issue persists, please contact the engineering team.'
      });
    }
  };

  const onSortEnd = ({oldIndex, newIndex}: {oldIndex: number; newIndex: number}) => {
    if (oldIndex === newIndex) return;
    const updated = [...orderedFaqs];
    const [moved] = updated.splice(oldIndex, 1);
    updated.splice(newIndex, 0, moved);
    setOrderedFaqs(updated);
  };

  const displayFaqs = isRearrangeMode ? orderedFaqs : subjectFaqs;

  const readOnlyQuestionsList = displayFaqs.map((subjectFAQ) => {
    if (!subjectFAQ.question) return null;
    return (
      <li className='pb-x2 content-link' key={subjectFAQ.id}>
        <Anchor as='a' href={`#${subjectFAQ.id}`}>
          {subjectFAQ.question}
        </Anchor>
      </li>
    );
  });

  const editButtons =
    sessionStore.hasContentManagerAccess() && displayFaqs.length > 1 ? (
      <ButtonGroup align='right'>
        {isRearrangeMode ? (
          <>
            <Button color='negative' onClick={discardChanges}>
              Discard changes
            </Button>
            <Button disabled={savePending} onClick={saveFAQOrder}>
              Save
            </Button>
          </>
        ) : (
          <Button onClick={setRearrangeMode}>Rearrange FAQs</Button>
        )}
      </ButtonGroup>
    ) : null;

  return (
    <div className='rounded-x1 border border-solid border-border-primary background-primary p-x4'>
      <Heading as='h2'>Frequently Asked Questions</Heading>
      {sessionStore.hasContentManagerAccess() && displayFaqs.length === 0 ? (
        <Banner>
          <Banner.Icon icon='exclamation' /> No FAQs have been created for this subject... yet! You
          can only see this message (and page) based on your elevated level of permissions.
        </Banner>
      ) : null}
      {isRearrangeMode ? (
        <SortableFAQList onSortEnd={onSortEnd} subjectFAQs={displayFaqs} />
      ) : (
        <ul className='pl-x4'>{readOnlyQuestionsList}</ul>
      )}
      {editButtons}
    </div>
  );
};

export default SubjectFAQQuestions;
